// ---------------------------------------------------------------------------
// Embed Widget Renderer
// ---------------------------------------------------------------------------
// SECURITY NOTE: The sandbox attribute includes both allow-scripts and
// allow-same-origin. Together, these allow the embedded page to programmatically
// remove its own sandbox via JavaScript. This is a known HTML spec tradeoff
// required for functional third-party tools (Calendly, Typeform, etc.).
// For untrusted content, consider a stricter sandbox without allow-same-origin.
// This component should only be used with trusted embed URLs from known providers.
// ---------------------------------------------------------------------------

import { registerRenderer } from "../registry.js";
import type { RenderContext } from "../types.js";
import { attr, text, url } from "../escape.js";

interface EmbedWidgetProps {
  heading?: string;
  embedUrl: string;
  height?: string;
  title: string;
  allowFullscreen?: boolean;
  aspectRatio?: "16:9" | "4:3" | "1:1" | "auto";
}

const ASPECT_RATIO_PADDING: Record<string, string> = {
  "16:9": "56.25%",
  "4:3": "75%",
  "1:1": "100%",
};

function render(props: Record<string, unknown>, _ctx: RenderContext): string {
  const p = props as unknown as EmbedWidgetProps;

  const embedUrl = p.embedUrl ?? "";
  const iframeTitle = p.title ?? "External embedded content";
  const height = p.height ?? "600px";
  const isFullscreen = p.allowFullscreen === true;
  const aspectRatio = p.aspectRatio ?? "auto";
  const providerHost = getProviderHost(embedUrl);
  const disclosureHtml = `
  <div class="ease-embed-disclosure" data-ease-integration-surface="external-embed" style="margin-bottom: 12px; display: flex; flex-wrap: wrap; gap: 8px; align-items: center;">
    <span style="display: inline-flex; align-items: center; border-radius: 999px; background: var(--ease-color-surface-muted, #f3f4f6); color: var(--ease-color-text, #111827); font-size: 0.72rem; font-weight: 700; letter-spacing: 0.08em; text-transform: uppercase; padding: 6px 10px;">External integration</span>
    <span style="font-size: 0.85rem; color: var(--ease-color-muted-foreground, #6b7280);">${providerHost ? `Hosted by ${text(providerHost)} inside a sandboxed iframe.` : "Hosted by an external provider inside a sandboxed iframe."}</span>
  </div>`;

  const headingHtml = p.heading
    ? `\n  <h2 class="ease-embed-heading">${text(p.heading)}</h2>`
    : "";

  const fullscreenAttr = isFullscreen ? " allowfullscreen" : "";

  const iframe = `<iframe src="${url(embedUrl)}" title="${attr(iframeTitle)}" class="ease-embed-iframe" sandbox="allow-scripts allow-forms allow-same-origin allow-popups" loading="lazy"${fullscreenAttr} style="border: 0; width: 100%; height: 100%;"></iframe>`;

  // Use aspect ratio wrapper or fixed height
  const paddingValue = ASPECT_RATIO_PADDING[aspectRatio];

  if (paddingValue) {
    return `<section class="ease-section ease-embed-widget" aria-label="${attr(`${iframeTitle} (external integration)`)}">${headingHtml}
  ${disclosureHtml}
  <div class="ease-embed-container" style="position: relative; width: 100%; padding-bottom: ${paddingValue}; overflow: hidden;">
    <div style="position: absolute; inset: 0;">
      ${iframe}
    </div>
  </div>
</section>`;
  }

  return `<section class="ease-section ease-embed-widget" aria-label="${attr(`${iframeTitle} (external integration)`)}">${headingHtml}
  ${disclosureHtml}
  <div class="ease-embed-container" style="width: 100%; height: ${attr(height)}; overflow: hidden;">
    ${iframe}
  </div>
</section>`;
}

registerRenderer("embed-widget", { render });

function getProviderHost(rawUrl: string): string | null {
  if (!rawUrl) {
    return null;
  }

  try {
    return new URL(rawUrl).host || null;
  } catch {
    return null;
  }
}
